import dynamic from "next/dynamic"
import {
	Theme,
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Typography,
	Box,
} from "@mui/material"
import { MdOutlineExpandMore } from "react-icons/md"

import { makeStyles } from "makeStyles"

import Ratings from "./components/Ratings"
import Description from "./components/Description"
import ConfigurationGroups from "./components/ConfigurationGroups"
import {
	ProductDetailPageReducerState,
	DispatchProductDetailPageReducer,
} from "@secureo/common/reducers/productDetailPageReducer"
const FAQ = dynamic(() => import("./components/FAQ"), { ssr: false })

import useTranslation from "hooks/useTranslation"

import { Review } from "@secureo/common/typings/Product"

const useStyles = makeStyles()((theme: Theme) => ({
	root: {
		width: "100%",
		color: theme.palette.secondary.dark,
	},
	heading: {
		fontSize: theme.typography.pxToRem(15),
		fontWeight: theme.typography.fontWeightRegular,
	},
	accordion: {
		border: "0 !important",
		color: theme.palette.secondary.dark,
		boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.15)",
	},
}))

const ProductDetailPageAccordion = ({
	reducerState,
	dispatch,
	accessToken,
	reviews,
	averageRating,
	language,
}: Props) => {
	const { classes } = useStyles()
	const { t } = useTranslation("product")

	const {
		productType,
		attributes: { configurationGroups = [] },
	} = reducerState

	const showConfigurationGroups = configurationGroups.length > 0

	return (
		<div className={classes.root}>
			<Box mt={4}>
				<Accordion defaultExpanded className={classes.accordion} id="productinformation">
					<AccordionSummary
						expandIcon={<MdOutlineExpandMore size="1.2rem" />}
						aria-controls="panel1a-content"
						id="panel1a-header"
					>
						<Typography className={classes.heading}>
							{t("productInformation")}
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Description reducerState={reducerState} language={language} />
					</AccordionDetails>
				</Accordion>
				{accessToken && showConfigurationGroups && (
					<Accordion defaultExpanded className={classes.accordion} id="configuration">
						<AccordionSummary
							expandIcon={<MdOutlineExpandMore size="1.2rem" />}
							aria-controls="panel2a-content"
							id="panel2a-header"
						>
							<Typography className={classes.heading}>{t("configure")}</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<ConfigurationGroups
								accessToken={accessToken}
								reducerState={reducerState}
								dispatch={dispatch}
								language={language}
							/>
						</AccordionDetails>
					</Accordion>
				)}
				<Accordion defaultExpanded className={classes.accordion} id="ratings">
					<AccordionSummary
						expandIcon={<MdOutlineExpandMore size="1.2rem" />}
						aria-controls="panel2a-content"
						id="panel2a-header"
					>
						<Typography className={classes.heading}>{t("ratings")}</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Ratings reviews={reviews} averageRating={averageRating} />
					</AccordionDetails>
				</Accordion>
				<Accordion defaultExpanded className={classes.accordion} id="faq">
					<AccordionSummary
						expandIcon={<MdOutlineExpandMore size="1.2rem" />}
						aria-controls="panel2a-content"
						id="panel2a-header"
					>
						<Typography className={classes.heading}>
							{t("questionsAndAnswers")}
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<FAQ productType={productType} language={language} />
					</AccordionDetails>
				</Accordion>
			</Box>
		</div>
	)
}

interface Props {
	accessToken: string
	reducerState: ProductDetailPageReducerState
	dispatch: DispatchProductDetailPageReducer
	averageRating: number
	reviews: Review[]
	language: string
}

export default ProductDetailPageAccordion
